import { useState, useRef, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Video, Mic, Square, Play, Pause, X, Check, Timer, Trash2, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

// Spinning Plumbob for recording indicator
const RecordingIndicator = () => (
  <motion.div
    className="w-4 h-4 rounded-full bg-red-500"
    animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
    transition={{ duration: 1, repeat: Infinity }}
  />
);

// Audio visualizer bars with pre-computed values
const AudioBars = ({ isRecording, isPaused }) => {
  const barHeights = useMemo(() => [35, 50, 42, 58, 45], []);
  const durations = useMemo(() => [0.35, 0.42, 0.38, 0.45, 0.4], []);
  
  return (
    <motion.div 
      className="flex gap-1"
      animate={isRecording && !isPaused ? { scale: [1, 1.1, 1] } : {}}
      transition={{ duration: 0.5, repeat: Infinity }}
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <motion.div
          key={i}
          className="w-3 bg-gradient-to-t from-[#2B4C7E] to-[#3A5C8F] rounded-full"
          animate={isRecording && !isPaused ? { 
            height: [20, barHeights[i], 20] 
          } : { height: 20 }}
          transition={{ 
            duration: durations[i], 
            repeat: Infinity,
            delay: i * 0.1
          }}
        />
      ))}
    </motion.div>
  );
};

export default function MediaRecorderModal({ 
  open, 
  onClose, 
  onSave, 
  type = 'video'
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [stream, setStream] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [facingMode, setFacingMode] = useState('user'); // 'user' = front camera, 'environment' = back camera
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const videoPreviewRef = useRef(null);
  const timerRef = useRef(null);

  // Flip camera function
  const flipCamera = async () => {
    if (isRecording || type !== 'video') return;
    
    // Stop current stream
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    
    // Toggle facing mode
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);
    
    try {
      const constraints = { 
        video: { facingMode: newFacingMode }, 
        audio: true 
      };
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
    } catch (error) {
      console.error('Error flipping camera:', error);
      // Fall back to any available camera
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        setStream(mediaStream);
      } catch (e) {
        console.error('Error accessing any camera:', e);
      }
    }
  };

  const retryInit = async () => {
    try {
      const constraints = type === 'video' 
        ? { video: { facingMode: 'user' }, audio: true }
        : { audio: true };
      
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      setPermissionDenied(false);
      setInitialized(true);
    } catch (error) {
      console.error('Error accessing media devices:', error);
      setPermissionDenied(true);
    }
  };

  // Set video preview when stream is available
  useEffect(() => {
    if (stream && type === 'video' && videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = stream;
    }
  }, [stream, type]);

  // Initialize media when modal opens
  useEffect(() => {
    let mounted = true;
    
    const init = async () => {
      if (open && !initialized) {
        try {
          const constraints = type === 'video' 
            ? { video: { facingMode: 'user' }, audio: true }
            : { audio: true };
          
          const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
          if (mounted) {
            setStream(mediaStream);
            setPermissionDenied(false);
            setInitialized(true);
          }
        } catch (error) {
          console.error('Error accessing media devices:', error);
          if (mounted) {
            setPermissionDenied(true);
          }
        }
      }
    };
    
    init();
    
    return () => {
      mounted = false;
    };
  }, [open, initialized, type]);

  // Cleanup on close or unmount
  useEffect(() => {
    if (!open && stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [open, stream]);

  const resetState = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setStream(null);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setDuration(0);
    setIsRecording(false);
    setIsPaused(false);
    setPermissionDenied(false);
    setInitialized(false);
    chunksRef.current = [];
    timerRef.current = null;
  };

  const startRecording = () => {
    if (!stream) return;
    
    chunksRef.current = [];
    const mimeType = type === 'video' 
      ? 'video/webm;codecs=vp8,opus'
      : 'audio/webm;codecs=opus';
    
    try {
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
    } catch (e) {
      mediaRecorderRef.current = new MediaRecorder(stream);
    }
    
    mediaRecorderRef.current.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };
    
    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(chunksRef.current, { 
        type: type === 'video' ? 'video/webm' : 'audio/webm' 
      });
      setRecordedBlob(blob);
      const url = URL.createObjectURL(blob);
      setRecordedUrl(url);
    };
    
    mediaRecorderRef.current.start(1000);
    setIsRecording(true);
    setDuration(0);
    
    timerRef.current = setInterval(() => {
      setDuration(prev => prev + 1);
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setDuration(prev => prev + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      }
      setIsPaused(!isPaused);
    }
  };

  const discardRecording = () => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setDuration(0);
    chunksRef.current = [];
  };

  const handleSave = () => {
    if (recordedBlob) {
      onSave(recordedBlob, type);
      resetState();
      onClose();
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-lg bg-gradient-to-br from-slate-900 via-[#0F1E36] to-slate-900 border-2 border-[#2B4C7E]/30 rounded-3xl text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl text-white">
            <motion.div 
              className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                type === 'video' 
                  ? 'bg-gradient-to-br from-[#C8A97E] to-[#B3956B]' 
                  : 'bg-gradient-to-br from-[#2B4C7E] to-[#2B4C7E]'
              }`}
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {type === 'video' ? <Video className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
            </motion.div>
            <span className="bg-gradient-to-r from-[#D0B384] to-[#3A5C8F] bg-clip-text text-transparent font-bold">
              {type === 'video' ? 'Record Video' : 'Record Voice Message'}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {permissionDenied ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <X className="h-10 w-10 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Permission Denied</h3>
              <p className="text-gray-400 text-sm mb-4">
                Please allow {type === 'video' ? 'camera and microphone' : 'microphone'} access to record.
              </p>
              <Button 
                onClick={() => {
                  setPermissionDenied(false);
                  retryInit();
                }}
                className="bg-[#2B4C7E] hover:bg-[#1E3A63] rounded-full"
              >
                Try Again
              </Button>
            </div>
          ) : (
            <>
              {/* Preview Area */}
              <div className={`relative rounded-2xl overflow-hidden ${
                type === 'video' 
                  ? 'aspect-video bg-black' 
                  : 'h-40 bg-gradient-to-br from-[#0F1E36]/50 to-[#0F1E36]/50'
              }`}>
                {type === 'video' ? (
                  <>
                    {!recordedUrl ? (
                      <>
                        <video
                          ref={videoPreviewRef}
                          autoPlay
                          muted
                          playsInline
                          className="w-full h-full object-cover"
                          style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                        />
                        {/* Camera Flip Button */}
                        {!isRecording && (
                          <motion.button
                            onClick={flipCamera}
                            className="absolute top-4 right-4 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center backdrop-blur-sm"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9, rotate: 180 }}
                            title="Flip camera"
                          >
                            <RefreshCw className="h-5 w-5 text-white" />
                          </motion.button>
                        )}
                      </>
                    ) : (
                      <video
                        src={recordedUrl}
                        controls
                        className="w-full h-full object-cover"
                      />
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    {recordedUrl ? (
                      <audio src={recordedUrl} controls className="w-4/5" />
                    ) : (
                      <AudioBars isRecording={isRecording} isPaused={isPaused} />
                    )}
                  </div>
                )}

                {/* Recording Indicator */}
                {isRecording && (
                  <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/50 px-3 py-1.5 rounded-full">
                    <RecordingIndicator />
                    <span className="text-sm font-mono">{formatDuration(duration)}</span>
                    {isPaused && <span className="text-yellow-400 text-xs">PAUSED</span>}
                  </div>
                )}
              </div>

              {/* Duration Display */}
              {!isRecording && recordedUrl && (
                <div className="flex items-center justify-center gap-2 text-gray-400">
                  <Timer className="h-4 w-4" />
                  <span className="text-sm">Duration: {formatDuration(duration)}</span>
                </div>
              )}

              {/* Controls */}
              <div className="flex items-center justify-center gap-4">
                {!recordedUrl ? (
                  <>
                    {!isRecording ? (
                      <motion.button
                        onClick={startRecording}
                        disabled={!stream}
                        className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg ${
                          type === 'video'
                            ? 'bg-gradient-to-br from-[#C8A97E] to-[#B3956B] hover:from-[#B3956B] hover:to-[#9C7F55]'
                            : 'bg-gradient-to-br from-[#2B4C7E] to-[#2B4C7E] hover:from-[#2B4C7E] hover:to-[#1E3A63]'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <div className="w-6 h-6 bg-white rounded-full" />
                      </motion.button>
                    ) : (
                      <div className="flex items-center gap-3">
                        <motion.button
                          onClick={pauseRecording}
                          className="w-12 h-12 rounded-full bg-yellow-500 hover:bg-yellow-600 flex items-center justify-center shadow-lg"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {isPaused ? <Play className="h-5 w-5 text-white ml-0.5" /> : <Pause className="h-5 w-5 text-white" />}
                        </motion.button>
                        <motion.button
                          onClick={stopRecording}
                          className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-lg"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Square className="h-6 w-6 text-white fill-white" />
                        </motion.button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-3">
                    <motion.button
                      onClick={discardRecording}
                      className="w-12 h-12 rounded-full bg-gray-600 hover:bg-gray-700 flex items-center justify-center shadow-lg"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Trash2 className="h-5 w-5 text-white" />
                    </motion.button>
                    <motion.button
                      onClick={handleSave}
                      className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg bg-gradient-to-br from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Check className="h-7 w-7 text-white" />
                    </motion.button>
                  </div>
                )}
              </div>

              {/* Instructions */}
              <p className="text-center text-gray-400 text-sm">
                {!isRecording && !recordedUrl && (
                  <>Press the {type === 'video' ? 'red' : 'blue'} button to start recording</>
                )}
                {isRecording && <>Recording in progress... Press square to stop</>}
                {recordedUrl && <>Review your recording, then save or discard</>}
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
